import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { Badge } from './badge.js';
import { Popover, PopoverTrigger, PopoverContent } from './popover.js';
import {
  PILL_BUTTON_BASE,
  PILL_BUTTON_ACTIVE,
  PILL_VALUE_CLASS,
} from './filter-pill-primitives.js';

export interface ScopeChipProps {
  /** Summarized value shown on the trigger (e.g. "Billboardy + Citylighty"). Translated by the caller. */
  label: string;
  /** Renders a read-only badge instead of an interactive popover (e.g. immutable/locked row). */
  disabled: boolean;
  /**
   * Popover body. Pass a plain node for multi-select content that should
   * stay open (e.g. checkboxes), or a function that receives a `close()`
   * callback for single-choice content that should auto-close right after a
   * selection (e.g. a radio group).
   */
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
}

/**
 * Generic trigger + popover "value chip" — a compact button showing a
 * summarized value that opens a popover editor on click. No domain
 * knowledge: label and popover content are fully supplied by the caller.
 *
 * Reuses the shared filter-pill button primitives
 * (`PILL_BUTTON_BASE`/`PILL_BUTTON_ACTIVE`/`PILL_VALUE_CLASS`) so it looks
 * consistent with the rest of the app's chip triggers (see
 * `CustomFilterPill` for the sibling filter-pill pattern).
 */
export function ScopeChip({ label, disabled, children }: ScopeChipProps) {
  const [open, setOpen] = React.useState(false);

  if (disabled) {
    return (
      <Badge variant="outline" className="gap-1 font-normal cursor-default text-[11px]">
        {label}
      </Badge>
    );
  }

  const close = () => setOpen(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={cn(PILL_BUTTON_BASE, PILL_BUTTON_ACTIVE)}>
          <span className={PILL_VALUE_CLASS}>{label}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        {typeof children === 'function' ? children(close) : children}
      </PopoverContent>
    </Popover>
  );
}
